import axios from "axios";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import url from "../../Url";
import ApiUtil from "../../utils/apiUtils";
import { 
  generateBrandPageTitle, 
  generateBrandDescription, 
  generateBrandCanonicalUrl, 
  generateBrandOgImage,
  generateBrandKeywords,
  generateBrandBreadcrumbStructuredData,
  generateBrandProductListStructuredData,
  generateBrandOrganizationStructuredData
} from "../../utils/seoUtils";
import Pagination01 from "../Pagination01";
import ProductCard from "../ProductCard";
import ProductFilters from "../ProductFilters";
import "./product.css";
import { useMediaQuery } from "@uidotdev/usehooks";
import FloatingButtonWithSidebar from "../sidebar/FloatingButtonWithSidebar";
import useBackwardCompatibleUrl from "../../hooks/useBackwardCompatibleUrl";
import SortBy from "../sortBy";

function BrandProductPage() {
  const { slug } = useParams();

  const [Product, setProduct] = useState([]);
  const [fetchedProduct, setFetchedProduct] = useState([]);
  const [queryParams, setQueryParams] = useState({});
  const [brand, setBrand] = useState();
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isMediumDevice = useMediaQuery("only screen and (min-width : 769px) and (max-width : 991px)");
  
  // Use our backward compatibility hook to handle URL transitions
  useBackwardCompatibleUrl({
    currentSlug: brand?.slug,
    currentName: brand?.brand_name,
    urlParam: slug,
    basePath: '/brand',
    shouldRedirect: true
  });

  const [selectedSortingOption, setSelectedSortingOption] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  const handlePageSelect = (page) => {
    setPagination((prev) => ({
      ...prev,
      page: page
    }));
    setCurrentPage(page)
  };

  const handleApplyClick = () => {
    if (minPrice || maxPrice) {
      priceFilterApply(minPrice, maxPrice);
    }
  };

  const handleResetClick = () => {
    priceFilterClear();
  };

  const queryString = useMemo(() => {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, v.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    });
    return searchParams.toString();
  }, [queryParams]);


  const handleFilterChange = useCallback((filterKey, filterValue, isChecked) => {
    debounce(() => {
      setQueryParams((prev) => {
        const updatedParams = { ...prev };
        const currentValues = Array.isArray(updatedParams[filterKey])
          ? updatedParams[filterKey]
          : [];

        if (isChecked) {
          updatedParams[filterKey] = [...currentValues, filterValue];
        } else {
          updatedParams[filterKey] = currentValues.filter((val) => val !== filterValue);
          if (updatedParams[filterKey].length === 0) delete updatedParams[filterKey];
        }
        return updatedParams;
      });
      setLoading(false);
    }, 500)();
  }, []);

  const getProducts = useCallback(async () => {
    let response;
    try {
      setLoading(true);
      if (!queryString ) return
      response = await ApiUtil.SendAsync({
        url: `/product/getAll?${queryString}`,
        params: pagination,
      });
      setProduct(response.data);
      setFetchedProduct(response.data?.data);
      setFilters(response.data?.filters);
    } catch (error) {
      response = error.response;
      setProduct([]);
      setFilters(response.data.filters)
    } finally {
      setLoading(false);
    }
  }, [queryString, pagination]);

  const fetchFilters = useCallback(async (catName) => {
    try {
      setLoading(true);
      await ApiUtil.SendAsync({ url: `/filter/brand/${catName}`, method: "GET" });
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  const getBrand = useCallback(async () => {
    try {
      // First try to get the brand using the /:name endpoint (handles both old and new URL formats)
      // Our backend has been updated to handle both "PureLux Floors" and "purelux-floors" formats
      const response = await axios.get(`${url}/api/brand/${slug}`);
      
      // If that fails, try the old /bySlug/:slug endpoint as fallback
      if (!response.data.data) {
        const slugResponse = await axios.get(`${url}/api/brand/bySlug/${slug}`);
        setBrand(slugResponse.data.data);
        fetchFilters(slugResponse.data.data?._id);
        setQueryParams((prev) => ({ ...prev, BrandName: slugResponse.data.data.brand_name }));
        return;
      }
      
      // Otherwise use the successful response
      setBrand(response.data.data);
      fetchFilters(response.data.data?._id);
      setQueryParams((prev) => ({ ...prev, BrandName: response.data.data.brand_name }));
    } catch (error) {
      console.error("Error fetching brand:", error);
      
      // As a last attempt, try the alternate endpoint
      try {
        // If the first request failed, try the other endpoint
        const alternateResponse = await axios.get(
          slug.includes('-') ? 
            `${url}/api/brand/bySlug/${slug}` : 
            `${url}/api/brand/${slug}`
        );
        
        if (alternateResponse.data.data) {
          setBrand(alternateResponse.data.data);
          fetchFilters(alternateResponse.data.data?._id);
          setQueryParams((prev) => ({ ...prev, BrandName: alternateResponse.data.data.brand_name }));
        }
      } catch (fallbackError) {
        console.error("All attempts to fetch brand failed:", fallbackError);
      }
    }
  }, [slug, fetchFilters]);

  const resetFilters = () => {
    setQueryParams({ BrandName: brand?.brand_name });
  };

  const priceFilterApply = (minPrice, maxPrice) => {
    setQueryParams((prev) => ({
      ...prev,
      minprice: minPrice,
      maxprice: maxPrice,
    }));
    setLoading(false);
  };

  const priceFilterClear = () => {
    setMinPrice("");
    setMaxPrice("");
    setQueryParams((prev) => ({
      ...prev,
      minprice: "",
      maxprice: "",
    }));
  };

  const handleSortChange = (event) => {
    const selectedOption = event.target.value;
    setSelectedSortingOption(selectedOption);

    let sortedProducts = [...fetchedProduct];
    switch (selectedOption) {
      case "lowToHigh":
        sortedProducts = fetchedProduct.sort((a, b) => a.price - b.price);
        break;
      case "highToLow":
        sortedProducts = fetchedProduct.sort((a, b) => b.price - a.price);
        break;
      case "BestSeller":
        let BestSellerTempFilter = {
          ...filters,
          BestSeller: true,
          IsmostViewed: "",
          newProduct: "",
        };
        sortedProducts = fetchedProduct.filter(
          (product) => product.BestSeller === true
        );
        setQueryParams((prev) => ({
          ...prev,
          BestSeller: true,
        }));
        break;
      case "MostViewed":
        sortedProducts = fetchedProduct.filter(
          (product) => product.IsmostViewed === true
        );
        setQueryParams((prev) => ({
          ...prev,
          IsmostViewed: true,
        }));
        break;
      case "NewItems":
        sortedProducts = fetchedProduct.filter(
          (product) => product.newProduct === true
        );
        setQueryParams((prev) => ({
          ...prev,
          newProduct: true,
        }));
        break;
      case "DiscountItems":
        sortedProducts = fetchedProduct.filter(
          (product) => product.isDiscount === true
        );
        setQueryParams((prev) => ({
          ...prev,
          isDiscount: true,
        }));
        break;
      default:
        break;
    }
    if (selectedOption === "all") {
      setQueryParams({ BrandName: brand?.brand_name });
    }
  };

  useEffect(() => {
    getBrand();
  }, [getBrand]);

  useEffect(() => {
    getProducts();
  }, [getProducts, queryParams]);

  return (
    <div>
      <Helmet>
        {/* Basic SEO Tags */}
        <title>{generateBrandPageTitle(brand?.brand_name)}</title>
        <meta name="description" content={generateBrandDescription(brand)} />
        <link rel="canonical" href={generateBrandCanonicalUrl(slug)} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={generateBrandPageTitle(brand?.brand_name)} />
        <meta property="og:description" content={generateBrandDescription(brand)} />
        <meta property="og:image" content={generateBrandOgImage(brand, Product?.data)} />
        <meta property="og:url" content={generateBrandCanonicalUrl(slug)} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Squarefoot Flooring" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={generateBrandPageTitle(brand?.brand_name)} />
        <meta name="twitter:description" content={generateBrandDescription(brand)} />
        <meta name="twitter:image" content={generateBrandOgImage(brand, Product?.data)} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="author" content="Squarefoot Flooring" />
        
        {/* Brand-specific meta tags */}
        {brand?.brand_name && (
          <meta name="keywords" content={generateBrandKeywords(brand)} />
        )}
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": generateBrandPageTitle(brand?.brand_name),
            "description": generateBrandDescription(brand),
            "url": generateBrandCanonicalUrl(slug),
            "mainEntity": generateBrandProductListStructuredData(brand, Product?.data, Product?.total),
            "about": generateBrandOrganizationStructuredData(brand, slug),
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": generateBrandBreadcrumbStructuredData(brand, slug, true)
            }
          })}
        </script>
      </Helmet>

      <div className="container">
        <div className="heading">
          <h1>{brand?.brand_name}</h1>
          <p>
            Explore our extensive collection of flooring products and tiles from
            the comfort of your home.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="row">
          {/* Filters and Showing Item on Mobile */}
          <div className="col-12 d-lg-none">
            <div className="showing-item ms-3">
              <span>{Product.total ?? 0} Products listed</span>
            </div>
            <hr className='d-lg-none'/>
          </div>
          {/* Desktop Layout */}
          <div className="col-lg-3 d-none d-lg-block">
            <ProductFilters
              filters={filters}
              handleFilterChange={handleFilterChange}
              collapseFilters={5}
              loading={loading}
              queryParams={queryParams}
              resetFilters={resetFilters}
            />
          </div>

          <div className="col-lg-9">
            <div className="container ps-4 pe-4">
              <div className="shop-topbar-wrapper pb-2 ps-2 pe-2 d-none d-lg-flex">
                <div className="shop-topbar-left">
                  <div className="showing-item">
                    <span>{Product.total ?? 0} Products listed</span>
                  </div>
                </div>
                <SortBy
                  selectedSortingOption={selectedSortingOption}
                  handleSortChange={handleSortChange}
                />
              </div>
            </div>
            <ProductCard Product={Product.data} loading={loading} category={brand?.brand_name} />
          </div>
          {Product?.pageCount > 0 && (
            <Pagination01
              productLength={Product.total}
              onNextPage={() => handlePageSelect(pagination.page + 1)}
              onPreviousPage={() => handlePageSelect(pagination.page - 1)}
              currentPage={currentPage}
              pageCount={Product.pageCount}
              onPageSelect={handlePageSelect}
            />
          )}
        </div>
      </div>
      {(isSmallDevice || isMediumDevice) && (
        <FloatingButtonWithSidebar
          onSortChange={handleSortChange}
          selectedSort={selectedSortingOption}
          productFilters={
            <ProductFilters
              filters={filters}
              handleFilterChange={handleFilterChange}
              collapseFilters={4}
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              minPrice={minPrice}
              setMinPrice={setMinPrice}
              maxPrice={maxPrice}
              setMaxPrice={setMaxPrice}
              handleApplyPrice={handleApplyClick}
              handlePriceReset={handleResetClick}
              loading={loading}
              resetFilters={resetFilters}
            />
          }
        />
      )}
    </div>
  );
}

export default BrandProductPage;
